import { Link } from 'react-router-dom'

import { useRegion } from 'components/app-shell/context/RegionContext'
import { tx } from 'lib/i18n'

import { Icon, IconName } from './Icon'

export type ComplianceStatus = 'onTime' | 'late' | 'missed' | 'upcoming' | 'none'

export interface ComplianceCell {
  status: ComplianceStatus
  href?: string
  title?: string
}

export interface ComplianceRow {
  siteId: string
  siteName: string
  cells: ComplianceCell[]
}

const STATUS_ICON: Partial<Record<ComplianceStatus, IconName>> = {
  late: 'clock',
  missed: 'close',
  none: 'minus',
  onTime: 'check',
}

const statusLabel = (status: ComplianceStatus, locale: 'ar' | 'en') =>
  ({
    late: tx(locale, 'متأخر', 'Late'),
    missed: tx(locale, 'مفقود', 'Missing'),
    none: tx(locale, 'لا ينطبق', 'Not applicable'),
    onTime: tx(locale, 'في الوقت', 'On time'),
    upcoming: tx(locale, 'لم يحن بعد', 'Not yet'),
  })[status]

const Day = ({ cell }: { cell: ComplianceCell }) => {
  const { locale } = useRegion()
  const icon = STATUS_ICON[cell.status]
  const label = cell.title || statusLabel(cell.status, locale)
  const content = (
    <span className={`compliance-day compliance-day--${cell.status}`} title={label}>
      {icon ? <Icon name={icon} size={14} /> : null}
    </span>
  )
  return cell.href ? (
    <Link to={cell.href} aria-label={label}>
      {content}
    </Link>
  ) : (
    content
  )
}

const SHORT_DAY: Record<string, string> = {
  Friday: 'Fri',
  Monday: 'Mon',
  Saturday: 'Sat',
  Sunday: 'Sun',
  Thursday: 'Thu',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  الأحد: 'أحد',
  الأربعاء: 'أرب',
  الاثنين: 'إثن',
  الثلاثاء: 'ثلا',
  الجمعة: 'جمع',
  الخميس: 'خمي',
  السبت: 'سبت',
}

const onTimeRate = (cells: ComplianceCell[]) => {
  const due = cells.filter((cell) => cell.status === 'onTime' || cell.status === 'late' || cell.status === 'missed')
  if (!due.length) return null
  const onTime = due.filter((cell) => cell.status === 'onTime').length
  return Math.round((onTime / due.length) * 100)
}

/** "Report submission this week" compliance strip — portfolio scope (Figma ComplianceCard). */
export const ComplianceCard = ({ dayLabels, rows }: { dayLabels: string[]; rows: ComplianceRow[] }) => {
  const { locale } = useRegion()
  return (
  <div className="compliance-card">
    <div className="compliance-card__row compliance-card__row--head">
      <span className="compliance-card__site" />
      <div className="compliance-card__days">
        {dayLabels.map((label) => (
          <span className="compliance-day-label" key={label} title={label}>
            {SHORT_DAY[label] ?? label}
          </span>
        ))}
      </div>
      <span className="compliance-card__rate" />
    </div>
    {rows.map((row) => {
      const rate = onTimeRate(row.cells)
      return (
        <div className="compliance-card__row" key={row.siteId}>
          <span className="compliance-card__site">{row.siteName}</span>
          <div className="compliance-card__days">
            {row.cells.map((cell, index) => (
              <Day cell={cell} key={index} />
            ))}
          </div>
          <span className="compliance-card__rate">{rate === null ? '—' : `${rate}%`}</span>
        </div>
      )
    })}
    <div className="compliance-card__legend">
      <span>
        <Icon name="check" size={12} /> {tx(locale, 'في الوقت', 'On time')}
      </span>
      <span>
        <Icon name="clock" size={12} /> {tx(locale, 'متأخر', 'Late')}
      </span>
      <span>
        <Icon name="close" size={12} /> {tx(locale, 'مفقود', 'Missing')}
      </span>
      <span className="compliance-day compliance-day--upcoming compliance-day--legend" /> {tx(locale, 'لم يحن بعد', 'Not yet')}
    </div>
  </div>
  )
}
